import * as React from 'react';
import * as _ from 'lodash';
import * as styles from './request.module.scss';
import * as commonStyles from '../../common/common.module.scss';
import moment from 'moment';
import { Redirect, withRouter, RouteComponentProps } from 'react-router-dom';
import Paths from '../../../models/enums/paths';
import { SupportContext } from '../../../shared/support-context';
import Textbox from '../../common/textbox';
import TypeField from '../../../models/typeField';
import PrimaryButton from '../../common/primary-button';
import SupportRequest from '../../../models/supportRequest';
import useSupport from '../../../hooks/useSupport';
import { AppContext } from '../../../shared/app-context';
import DatePicker from '../../common/date-picker';
import RadioForm from '../../common/radio-form';
import OutlinedDropdown from '../../common/outlinedDropdown';
import MultiSelectMenu from '../../common/multiSelectMenu';
import { CircularProgress } from '@material-ui/core';
import PriorityTwoSuccess from '../../announcement/priority-two-success';
import PriorityTwoFailure from '../../announcement/priority-two-failure';
import SupportTicket from '../../../models/supportTicket';
import DisabledInput from '../../common/disabled-input';
import ConfirmationModal from '../../report/customViewModal/confirmationModal';
import ConfirmationDialog from '../../common/confirmationDialog';
import { useRef } from 'react';
import TertiaryButton from '../../common/tertiary-button';

const RequestDetails = ({ history }: RouteComponentProps) => {
  const { allFilters } = React.useContext(SupportContext);
  if (allFilters.types.length < 1) {
    return <Redirect to={Paths.Support} />;
  }
  const [ticketType, setTicketType] = React.useState(0);
  const [values, setValues] = React.useState<TypeField[]>(_.cloneDeep(allFilters.types[0].fields));
  const { addSupportTicket } = useSupport();
  const [loading, setLoading] = React.useState<boolean>(false);
  const [requestSuccessful, setRequestSuccessful] = React.useState<boolean>(null);
  const [requestResponse, setRequestResponse] = React.useState<SupportTicket>(null);
  const appContext = React.useContext(AppContext);

  const [displayDirtyWarning, setDisplayDirtyWarning] = React.useState<boolean>(false);
  const [confirmedNavigation, setConfirmedNavigation] = React.useState<boolean>(false);
  const [blockedLocation, setBlockedLocation] = React.useState<string>('');

  const unblock = useRef(null);

  const isDirty = () => {
    //Get data
    const typeCopy = _.cloneDeep(allFilters.types[ticketType]);
    typeCopy.fields = _.cloneDeep(values);
    typeCopy.fields.map(item => {
      if (item.values && item.id !== 'attachment') {
        const stringValues = item.values.map(val => val.value || val);
        item.values = stringValues;
      }
      return item;
    });

    const data: SupportRequest = {
      requestTypeId: typeCopy.value,
      requestTypes: [typeCopy],
      serviceDeskOrganizationId: '4'
    };

    //Request type changed from the default 1 or a field is no longer null
    if (data.requestTypeId !== '1' || !_.every(data.requestTypes[0].fields, { value: null })) {
      return true;
    } else {
      return false;
    }
  };

  React.useEffect(() => {
    unblock.current = history.block(location => {
      if (isDirty() && !requestSuccessful) {
        setDisplayDirtyWarning(true);
        setBlockedLocation(location.pathname);
        return false;
      } else {
        unblock.current();
      }
    });
  }, [requestSuccessful]);

  React.useEffect(() => {
    if (confirmedNavigation) {
      unblock.current();
      history.push(blockedLocation);
    }
  }, [confirmedNavigation]);

  const handleCancel = (dirty: boolean) => {
    if (!dirty) {
      unblock.current();
    }
    history.push(Paths.Support);
  };

  const handleSubmit = event => {
    event && event.preventDefault();
    event && event.persistData();
    const typeCopy = _.cloneDeep(allFilters.types[ticketType]);
    typeCopy.fields = values;
    typeCopy.fields.map(item => {
      if (item.values && item.id !== 'attachment') {
        const stringValues = item.values.map(val => val.value || val);
        item.values = stringValues;
      }
      return item;
    });

    const data: SupportRequest = {
      requestTypeId: typeCopy.value,
      requestTypes: [typeCopy],
      serviceDeskOrganizationId: '4'
    };

    setLoading(true);

    addSupportTicket(appContext.state.clientId, data)
      .then(ticket => {
        setLoading(false);
        setRequestResponse(ticket);
        setRequestSuccessful(true);
        setDisplayDirtyWarning(false);
      })
      .catch(() => {
        setRequestSuccessful(false);
        setLoading(false);
      });
  };

  const renderTypes = () => {
    return allFilters.types[ticketType].fields.map((field: TypeField, index: number) => {
      switch (field.type) {
        case 'string': {
          return (
            <div style={{ height: 98, width: 600 }}>
              <Textbox
                key={`field-${index}`}
                value={values[index].value ? values[index].value : ''}
                label={field.name}
                multiline={true}
                variant="outlined"
                required={field.required}
                error={field.required && !values[index].value && values[index].touched}
                onChange={event => {
                  event.persist();
                  setValues(arr => {
                    const newArray = [...arr];
                    newArray[index].value = event.target.value;
                    newArray[index].touched = true;
                    return newArray;
                  });
                }}
              />
            </div>
          );
        }
        case 'date': {
          return (
            <DatePicker
              key={`field-${index}`}
              date={values[index].value ? values[index].value : null}
              label={field.name}
              changeDate={newDate => {
                setValues(arr => {
                  const newArray = [...arr];
                  newArray[index].value = !newDate ? null : moment(newDate).format('YYYY-MM-DD');
                  return newArray;
                });
              }}
            />
          );
        }
        case 'priority':
        case 'option': {
          if (field.validValues.length <= 4) {
            return (
              <RadioForm
                key={`field-${index}`}
                values={field.validValues}
                options={field.validValues.map(item => item.text)}
                title={field.name}
                selectedValue={values[index].value}
                handleChange={val => {
                  setValues(arr => {
                    const newArray = [...arr];
                    newArray[index].value = val;
                    return newArray;
                  });
                }}
              />
            );
          }
          return (
            <div key={`field-${index}`} style={{ width: 290, height: 40 }}>
              <OutlinedDropdown
                values={field.validValues}
                title={field.name}
                selectedValue={values[index].value}
                handleChange={val => {
                  setValues(arr => {
                    const newArray = [...arr];
                    newArray[index].value = val;
                    return newArray;
                  });
                }}
              />
            </div>
          );
        }
        case 'array': {
          if (field.id === 'attachment') {
            return <div key={`field-${index}`} style={{ display: 'none' }} />;
          }
          return (
            <MultiSelectMenu
              key={`field-${index}`}
              label={field.name}
              values={field.validValues}
              options={field.validValues.map(item => item.text)}
              selectedValues={values[index].values || []}
              handleChange={val => {
                setValues(arr => {
                  const newArray = [...arr];
                  newArray[index].values = val;
                  return newArray;
                });
              }}
            />
          );
        }
        case 'option-with-child': {
          return (
            <div key={`field-${index}`} style={{ width: 290, height: 40 }}>
              <OutlinedDropdown
                values={field.validValues}
                title={field.name}
                selectedValue={values[index].value}
                handleChange={val => {
                  setValues(arr => {
                    const newArray = [...arr];
                    newArray[index].value = val;
                    return newArray;
                  });
                }}
              />
            </div>
          );
        }
      }
    });
  };

  const renderAfterSubmit = () => {
    return values.map((field, index) => {
      const selectValidValue = () => {
        switch (field.type) {
          case 'string':
          case 'date':
            return field.value;
          case 'priority':
          case 'option-with-child':
          case 'option':
            const text = field.validValues.filter(val => val.value === field.value)[0].text;
            return text;
          case 'array':
            const values = field.values.map(val => {
              return field.validValues.find(item => item.value === val).text;
            });
            return values.join(', ');
        }
      };

      if (field.value) {
        return (
          <DisabledInput key={index} label={field.name} value={selectValidValue()} disabled={true} multiline={true} />
        );
      } else if (field.values && field.values.length > 0) {
        return (
          <DisabledInput key={index} label={field.name} value={selectValidValue()} disabled={true} multiline={true} />
        );
      }
      return null;
    });
  };

  const renderTypeOptions = () => {
    return (
      <DisabledInput label="What can we help you with?" value={allFilters.types[ticketType].text} disabled={true} />
    );
  };

  const isSubmitDisabled =
    values
      .map(val => {
        if (
          (val.required && !val.value) ||
          (val.type === 'date' && val.value === 'Invalid date' && !moment(val.value).isValid())
        ) {
          return true;
        }
        return false;
      })
      .filter(val => val).length > 0;

  return (
    <div className={styles.addRequestPage}>
      <ConfirmationModal
        open={isDirty() && displayDirtyWarning}
        closeModal={() => {}}
        children={
          <ConfirmationDialog
            title="Confirmation"
            message="You have unsaved changes. Are you sure you would like to leave?"
            confirmAction={() => {
              setConfirmedNavigation(true);
              history.push(blockedLocation);
            }}
            cancelAction={() => {
              setDisplayDirtyWarning(false);
            }}
          />
        }
      />
      <div className={styles.requestContainer}>
        {requestSuccessful && (
          <div style={{ width: '97%' }}>
            <PriorityTwoSuccess
              message={`Support Request Submitted. Your reference is ${requestResponse.issueName}.`}
            />
          </div>
        )}
        {requestSuccessful === false && (
          <div style={{ width: '97%' }}>
            <PriorityTwoFailure message="Unable to add support ticket. Please try again or contact your system administrator." />
          </div>
        )}
        <div className={styles.header}>
          <h5> Submit a Request</h5>
        </div>
        {loading && (
          <div className={styles.circularProgress}>
            <CircularProgress />
          </div>
        )}
        <form className={loading ? styles.loading : styles.requestSection} onSubmit={event => handleSubmit(event)}>
          <div style={{ width: 290, height: 40 }}>
            {requestSuccessful ? (
              renderTypeOptions()
            ) : (
              <OutlinedDropdown
                values={allFilters.types}
                title="What can we help you with?"
                selectedValue={allFilters.types[ticketType].value}
                handleChange={val => {
                  const newIndex = allFilters.types.findIndex(item => val === item.value);
                  setTicketType(newIndex);
                  setValues(_.cloneDeep(allFilters.types[newIndex].fields));
                }}
              />
            )}
          </div>
          {requestSuccessful ? renderAfterSubmit() : renderTypes()}
          {requestSuccessful !== true && (
            <div className={commonStyles.formActions}>
              <TertiaryButton
                text="Cancel"
                title="Cancel"
                handleClick={() => {
                  handleCancel(isDirty());
                }}
              />
              <span className={commonStyles.submitButton}>
                <PrimaryButton
                  text="Submit"
                  title="Submit"
                  deactive={isSubmitDisabled}
                  handleClick={event => handleSubmit(event)}
                />
              </span>
            </div>
          )}
        </form>
      </div>
      <div className={styles.supportArea}>
        {requestSuccessful !== true && (
          <div className={styles.supportInfo}>
            <h2>Prefer to send a request by Email?</h2>
            <h3>Contact Customer Service</h3>
            <a href={'mailto:' + appContext.state.supportEmail} target="_blank">
              Email: {appContext.state.supportEmail}
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

export default withRouter(RequestDetails);
